import { FC, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { useFeatureFlags } from '@features/feature-flags';
import { FeatureFlagKey } from '@libs/constants';

interface Props {
  feature: FeatureFlagKey;
  children: ReactNode;
}

/**
 * Gate for a runtime-flagged feature's routes. Redirects to the dashboard
 * once the flag resolves to off.
 *
 * Waits for the flags request to settle before deciding: `useFeatureFlag`'s
 * fail-closed default is right for hiding a nav item, but applied here it
 * would redirect away on every fresh load of an enabled feature for the
 * moment before the response arrives. The backend independently enforces
 * this via `@RequiresFeature()` — this is a UX guard, not the security
 * boundary.
 */
export const FeatureGuard: FC<Props> = observer(({ feature, children }) => {
  const { data, isLoading } = useFeatureFlags();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!(data?.[feature] ?? false)) {
    return <Navigate to={routes.dashboard()} replace />;
  }

  return <>{children}</>;
});
